/**
 * The state every report page set up for itself: the time-range presets, the range a page
 * opens on, the error-code messages, and what survives a campus change
 * (docs/DUPLICATION_AUDIT.md, B15).
 */

import { getToday, getWeekRange, getMonthRange, getQuarterRange } from '../libs.jsx';

/* Ids 1-8 are shared with att_report_intervals (a schedule's report_interval_id) and with
   the rangeId in a restored report's cache key, so they must not be renumbered. Ranges that
   end "now" run to the current moment; the rest end at 23:59:59.999. */
export const TIME_RANGES = [
    { id: 1, label: 'Today', getRange: () => { const d = getToday(); d.setHours(0, 0, 0, 0); return [d, new Date()]; } },
    { id: 2, label: 'Yesterday', getRange: () => { const d = getToday(-1); d.setHours(0, 0, 0, 0); const e = getToday(-1); e.setHours(23, 59, 59, 999); return [d, e]; } },
    { id: 3, label: 'This Week', getRange: () => { const [s] = getWeekRange(0); s.setHours(0, 0, 0, 0); return [s, new Date()]; } },
    { id: 4, label: 'This Month', getRange: () => { const [s] = getMonthRange(0); s.setHours(0, 0, 0, 0); return [s, new Date()]; } },
    { id: 5, label: 'This Quarter', getRange: () => { const [s] = getQuarterRange(0); s.setHours(0, 0, 0, 0); return [s, new Date()]; } },
    { id: 6, label: 'Last Week', getRange: () => { const [s, e] = getWeekRange(-1); s.setHours(0, 0, 0, 0); e.setHours(23, 59, 59, 999); return [s, e]; } },
    { id: 7, label: 'Last Month', getRange: () => { const [s, e] = getMonthRange(-1); s.setHours(0, 0, 0, 0); e.setHours(23, 59, 59, 999); return [s, e]; } },
    { id: 8, label: 'Last Quarter', getRange: () => { const [s, e] = getQuarterRange(-1); s.setHours(0, 0, 0, 0); e.setHours(23, 59, 59, 999); return [s, e]; } },
];

/** { 1: 'Today', … 8: 'Last Quarter' } */
export const RANGE_NAMES = Object.fromEntries(TIME_RANGES.map((r) => [r.id, r.label]));

/** Yesterday: where every report page with a range picker opens. */
export const DEFAULT_RANGE_ID = 2;

/**
 * The page's three range fields for a range id. Anything that is not a preset (0, as a
 * Custom run stores it) is Custom, with rangeId 0.
 *
 * @returns {{ rangeId: number, rangeName: string, customRange: boolean }}
 */
export function rangeStateFor(rangeId) {
    return RANGE_NAMES[rangeId]
        ? { rangeId, rangeName: RANGE_NAMES[rangeId], customRange: false }
        : { rangeId: 0, rangeName: 'Custom', customRange: true };
}

/**
 * The range a page opens on, from its navigation state:
 * - a preset rangeId (Run Report Local, or a restored preset run) → that preset;
 * - dates with no preset (a restored Custom run, the hourly drill-down, Run Detailed
 *   Report) → Custom. These used to read "Yesterday" on most pages, and the wrong rangeId
 *   also missed the restore cache;
 * - otherwise → Yesterday.
 */
export function initialRangeState(navState, defaultId = DEFAULT_RANGE_ID) {
    if (navState?.rangeId && RANGE_NAMES[navState.rangeId]) return rangeStateFor(navState.rangeId);
    if (navState?.startDate) return rangeStateFor(0);
    return rangeStateFor(defaultId);
}

/* postReport's error codes (services/api.js): 11 = 503, 10 = 500 or a failed request,
   12 = 403. The wording is V1's. */
const REPORT_ERROR_MESSAGES = {
    11: 'Report timed out. Contact your customer success manager for large reports.',
    10: 'An unknown error occurred. Please contact customer success if the problem persists.',
    12: 'Request unauthorized. Service may be unavailable.',
};

/** The toast text for a report ErrorCode. */
export function reportErrorMessage(code) {
    return REPORT_ERROR_MESSAGES[code] || 'Report request failed.';
}

/**
 * Report filters after a campus change. Most lists hold the previous campus's ids (areas,
 * residents, users, devices, event types, reasons), which the new campus's options cannot
 * show or uncheck, but the report still sends them. So every list is emptied; the settings
 * that are not ids (responseTime, timeWindow) carry over. Works for every page's filter
 * shape. Pass it to the setter: setReportFilters(clearCampusFilters).
 */
export function clearCampusFilters(filters) {
    return Object.fromEntries(Object.entries(filters || {}).map(([k, v]) => [k, Array.isArray(v) ? [] : v]));
}
